import { Delete, Space, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

type KeyboardInput = HTMLInputElement | HTMLTextAreaElement;

const letterRows = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["Q", "W", "E", "R", "T", "Z", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Y", "X", "C", "V", "B", "N", "M", "\u00c4", "\u00d6", "\u00dc", "\u00df"]
];

const writableInputTypes = new Set(["", "text", "search", "password", "email", "tel", "url", "number"]);

export function TouchKeyboard({ enabled }: { enabled: boolean }) {
  const keyboardRef = useRef<HTMLDivElement>(null);
  const [target, setTarget] = useState<KeyboardInput | null>(null);
  const [caps, setCaps] = useState(true);

  useEffect(() => {
    if (!enabled) {
      setTarget(null);
      return;
    }

    function onFocusIn(event: FocusEvent) {
      const next = keyboardTarget(event.target);
      if (next) setTarget(next);
      else if (event.target instanceof HTMLInputElement && event.target.classList.contains("rfid-capture")) setTarget(null);
    }

    function onPageClick(event: MouseEvent) {
      const node = event.target instanceof Node ? event.target : null;
      if (node && keyboardRef.current?.contains(node)) return;
      if (keyboardTarget(event.target)) return;
      window.setTimeout(() => {
        if (!keyboardTarget(document.activeElement)) setTarget(null);
      }, 0);
    }

    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("click", onPageClick);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("click", onPageClick);
    };
  }, [enabled]);

  useEffect(() => {
    document.body.classList.toggle("touch-keyboard-open", enabled && Boolean(target));
    return () => document.body.classList.remove("touch-keyboard-open");
  }, [enabled, target]);

  if (!enabled || !target) return null;

  function pressCharacter(key: string) {
    if (!target || !document.contains(target)) {
      setTarget(null);
      return;
    }
    const char = caps && key !== "\u00df" ? key.toUpperCase() : key.toLowerCase();
    insertText(target, char);
  }

  function backspace() {
    if (!target || !document.contains(target)) {
      setTarget(null);
      return;
    }
    removeText(target);
  }

  function clear() {
    if (!target || !document.contains(target)) {
      setTarget(null);
      return;
    }
    writeValue(target, "", 0);
  }

  function done() {
    target?.blur();
    setTarget(null);
  }

  return (
    <div className="touch-keyboard" ref={keyboardRef} aria-label="Bildschirmtastatur">
      {(target.inputMode === "numeric" ? [["1", "2", "3"], ["4", "5", "6"], ["7", "8", "9"], ["0"]] : letterRows).map((row) => (
        <div className="touch-keyboard-row" key={row.join("")}>
          {row.map((key) => (
            <button className="touch-key" key={key} onClick={() => pressCharacter(key)} onPointerDown={keepInputFocus} type="button">
              {caps && key !== "\u00df" ? key.toUpperCase() : key.toLowerCase()}
            </button>
          ))}
        </div>
      ))}
      <div className="touch-keyboard-row">
        {target.inputMode !== "numeric" && <button className={`touch-key wide ${caps ? "active" : ""}`} onClick={() => setCaps((current) => !current)} onPointerDown={keepInputFocus} type="button">
          {"Gro\u00df/Klein"}
        </button>}
        {target.inputMode !== "numeric" && <button className="touch-key space" onClick={() => insertText(target, " ")} onPointerDown={keepInputFocus} type="button">
          <Space />
          Leerzeichen
        </button>}
        <button className="touch-key wide" onClick={backspace} onPointerDown={keepInputFocus} type="button">
          <Delete />
          Korrigieren
        </button>
        <button className="touch-key wide danger" onClick={clear} onPointerDown={keepInputFocus} type="button">
          <X />
          {"L\u00f6schen"}
        </button>
        <button className="touch-key wide done" onClick={done} onPointerDown={keepInputFocus} type="button">
          Fertig
        </button>
      </div>
    </div>
  );
}

function keyboardTarget(value: EventTarget | null): KeyboardInput | null {
  if (value instanceof HTMLInputElement) {
    if (!writableInputTypes.has(value.type)) return null;
    if (value.classList.contains("rfid-capture")) return null;
    if (value.disabled || value.readOnly) return null;
    return value;
  }
  if (value instanceof HTMLTextAreaElement && !value.disabled && !value.readOnly) return value;
  return null;
}

function keepInputFocus(event: ReactPointerEvent<HTMLButtonElement>) {
  event.preventDefault();
}

function insertText(input: KeyboardInput, text: string) {
  const value = input.value;
  const { start, end } = selection(input);
  const next = `${value.slice(0, start)}${text}${value.slice(end)}`;
  writeValue(input, next, start + text.length);
}

function removeText(input: KeyboardInput) {
  const value = input.value;
  const { start, end } = selection(input);
  if (start !== end) {
    writeValue(input, `${value.slice(0, start)}${value.slice(end)}`, start);
    return;
  }
  if (start === 0) return;
  writeValue(input, `${value.slice(0, start - 1)}${value.slice(start)}`, start - 1);
}

function selection(input: KeyboardInput): { start: number; end: number } {
  try {
    return {
      start: input.selectionStart ?? input.value.length,
      end: input.selectionEnd ?? input.value.length
    };
  } catch {
    return { start: input.value.length, end: input.value.length };
  }
}

function writeValue(input: KeyboardInput, value: string, cursor: number) {
  const prototype = input instanceof HTMLTextAreaElement ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;
  if (setter) setter.call(input, value);
  else input.value = value;
  input.dispatchEvent(new Event("input", { bubbles: true }));
  window.requestAnimationFrame(() => {
    input.focus({ preventScroll: true });
    try {
      input.setSelectionRange(cursor, cursor);
    } catch {
      // Some input types, such as number, do not support text selection.
    }
  });
}
